import { useEffect, useRef, useState } from "react";

const PALETTE = ["#F5C542", "#FF8C42", "#FF5D8F", "#7BE0A3", "#FFF3D6", "#FFB4A2"];

/**
 * Grand fireworks show that plays once when the parent opens, then settles
 * and unmounts. Canvas overlay — position it inside a `relative` container.
 * Skipped entirely for visitors who prefer reduced motion.
 */
export const Fireworks = ({ duration = 7000, className = "" }) => {
  const canvasRef = useRef(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDone(true);
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let W = 0;
    let H = 0;
    let raf;
    let rockets = [];
    let sparks = [];
    let lastLaunch = 0;
    const start = performance.now();

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const launch = () => {
      rockets.push({
        x: W * (0.1 + Math.random() * 0.8),
        y: H + 8,
        vx: (Math.random() - 0.5) * 1.1,
        vy: -(6.4 + Math.random() * 2.6),
        targetY: H * (0.14 + Math.random() * 0.34),
        color: PALETTE[(Math.random() * PALETTE.length) | 0],
      });
    };

    const explode = (r) => {
      const n = 60 + ((Math.random() * 40) | 0);
      for (let i = 0; i < n; i++) {
        const a = (Math.PI * 2 * i) / n + Math.random() * 0.3;
        const speed = 1.6 + Math.random() * 3.8;
        sparks.push({
          x: r.x,
          y: r.y,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed,
          life: 1,
          decay: 0.008 + Math.random() * 0.012,
          color: Math.random() < 0.75 ? r.color : PALETTE[(Math.random() * PALETTE.length) | 0],
          size: 1.1 + Math.random() * 1.6,
        });
      }
    };

    const frame = (now) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = "lighter";

      if (elapsed < duration && now - lastLaunch > 220 + Math.random() * 280) {
        launch();
        lastLaunch = now;
        if (Math.random() < 0.3) setTimeout(launch, 130);
      }

      rockets = rockets.filter((r) => {
        r.x += r.vx;
        r.y += r.vy;
        r.vy += 0.05;
        ctx.globalAlpha = 1;
        ctx.strokeStyle = r.color;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(r.x, r.y);
        ctx.lineTo(r.x - r.vx * 3, r.y - r.vy * 3);
        ctx.stroke();
        ctx.fillStyle = "#FFF3D6";
        ctx.beginPath();
        ctx.arc(r.x, r.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
        if (r.y <= r.targetY || r.vy > -1) {
          explode(r);
          return false;
        }
        return true;
      });

      sparks = sparks.filter((s) => {
        s.x += s.vx;
        s.y += s.vy;
        s.vy += 0.032;
        s.vx *= 0.985;
        s.life -= s.decay;
        if (s.life <= 0) return false;
        ctx.globalAlpha = Math.max(s.life, 0);
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        ctx.fill();
        return true;
      });

      ctx.globalAlpha = 1;
      if (elapsed < duration + 4000 || rockets.length || sparks.length) {
        raf = requestAnimationFrame(frame);
      } else {
        setDone(true);
      }
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [duration]);

  if (done) return null;
  return (
    <canvas
      ref={canvasRef}
      data-testid="fireworks-canvas"
      aria-hidden="true"
      className={`absolute inset-0 pointer-events-none ${className}`}
    />
  );
};
